<template>
  <div class="fleet-pressure">
    <header class="fleet-pressure__head">
      <div>
        <h1>Fleet pressure</h1>
        <p>
          Live host resources across every reporting server, sorted by whatever
          is closest to hurting.
          <span v-if="generatedLabel"> Updated {{ generatedLabel }}.</span>
        </p>
      </div>
      <router-link class="fleet-pressure__back" to="/servers">Fleet inventory →</router-link>
    </header>

    <p v-if="loadError" class="fleet-pressure__error">{{ loadError }}</p>
    <p v-else-if="loading && !servers.length" class="fleet-pressure__empty">Loading telemetry…</p>
    <p v-else-if="!servers.length" class="fleet-pressure__empty">
      No server has reported host resource telemetry yet. Agents older than
      2026-08-01 don't send it — nothing is wrong, there is simply nothing to show.
    </p>

    <!-- Leading server gets the full per-resource breakdown; the rest collapse
         into a compact table. The view answers "which box is about to fall
         over", and the answer deserves more room than the boxes that aren't. -->
    <section
      v-for="server in detailed"
      :key="server.server_id"
      class="fleet-pressure__card"
    >
      <header class="fleet-pressure__card-head">
        <router-link class="fleet-pressure__card-name" :to="`/servers/${server.server_id}`">
          {{ server.server }}
        </router-link>
        <span class="fleet-pressure__meta">
          {{ server.cores !== null ? `${server.cores} cores` : 'core count unknown' }} ·
          sampled {{ relativeAge(server.collected_age_seconds) }}
        </span>
        <span
          v-if="worstPill(server)"
          class="fleet-pressure__pill"
          :class="`is-${worstPill(server).tone}`"
        >{{ worstPill(server).label }}</span>
      </header>

      <div class="fleet-pressure__row">
        <span class="fleet-pressure__row-label">CPU</span>
        <span class="fleet-pressure__bar">
          <span
            class="fleet-pressure__bar-fill"
            :class="`is-${loadTone(server.load_per_core)}`"
            :style="{ width: `${loadWidth(server.load_per_core)}%` }"
          ></span>
        </span>
        <span class="fleet-pressure__row-value">
          {{ server.load_per_core !== null ? `${server.load_per_core.toFixed(2)} / core` : '—' }}
        </span>
        <span class="fleet-pressure__row-note">
          <template v-if="server.load_1m !== null">load {{ server.load_1m.toFixed(2) }}</template>
        </span>
      </div>

      <div class="fleet-pressure__row">
        <span class="fleet-pressure__row-label">Memory</span>
        <span class="fleet-pressure__bar">
          <span
            class="fleet-pressure__bar-fill"
            :class="`is-${pctTone(server.mem_pct)}`"
            :style="{ width: `${clampPct(server.mem_pct)}%` }"
          ></span>
        </span>
        <span class="fleet-pressure__row-value">
          {{ server.mem_pct !== null ? `${Math.round(server.mem_pct)}%` : '—' }}
        </span>
        <span class="fleet-pressure__row-note">{{ memoryNote(server) }}</span>
      </div>

      <div
        v-for="fs in filesystems(server)"
        :key="fs.mount"
        class="fleet-pressure__row"
      >
        <span class="fleet-pressure__row-label">Disk {{ fs.mount }}</span>
        <span class="fleet-pressure__bar">
          <span
            class="fleet-pressure__bar-fill"
            :class="`is-${pctTone(fs.usedPct)}`"
            :style="{ width: `${clampPct(fs.usedPct)}%` }"
          ></span>
        </span>
        <span class="fleet-pressure__row-value">{{ Math.round(fs.usedPct) }}%</span>
        <span class="fleet-pressure__row-note">
          {{ formatBytes(fs.usedBytes) }} of {{ formatBytes(fs.totalBytes) }}
        </span>
      </div>

      <div
        v-for="pool in server.fpm_pools"
        :key="`${pool.php_version || '?'}:${pool.pool}`"
        class="fleet-pressure__row"
      >
        <span class="fleet-pressure__row-label">FPM pool</span>
        <span class="fleet-pressure__bar">
          <span
            class="fleet-pressure__bar-fill"
            :class="`is-${poolTone(pool)}`"
            :style="{ width: `${poolWidth(pool)}%` }"
          ></span>
        </span>
        <!-- The ratio, not a percentage alone: "3/3" is instantly legible
             where "100%" invites the question "of what?". -->
        <span class="fleet-pressure__row-value fleet-pressure__row-value--wide">
          {{ poolLabel(pool) }}
          <template v-if="pool.active_workers !== null && pool.max_children !== null">
            <strong>{{ pool.active_workers }}/{{ pool.max_children }}</strong>
          </template>
          <template v-else-if="pool.active_workers !== null">
            <strong>{{ pool.active_workers }}</strong> worker(s)
          </template>
        </span>
        <span class="fleet-pressure__row-note">
          <span v-if="pool.saturation_fault" class="fleet-pressure__suspect" :title="SUSPECT_HINT">
            reading suspect
          </span>
          <span v-else-if="pool.saturation_pct !== null">
            {{ Math.round(pool.saturation_pct) }}% · {{ methodLabel(pool.method) }}
          </span>
          <span v-else>{{ methodLabel(pool.method) }}</span>
        </span>
        <span
          v-if="!pool.saturation_fault && pool.saturation_pct !== null && pool.saturation_pct >= 90"
          class="fleet-pressure__pill is-bad"
        >Saturated</span>
      </div>

      <div v-if="server.busiest_vhost" class="fleet-pressure__vhost">
        <span class="fleet-pressure__stripe" :class="`is-${vhostTone(server.busiest_vhost)}`"></span>
        <span class="fleet-pressure__vhost-body">
          <span class="fleet-pressure__vhost-title">
            <strong>Busiest vhost</strong> — {{ server.busiest_vhost.domain }}
            <template v-if="server.busiest_vhost.requests_per_minute !== null">
              · {{ Math.round(server.busiest_vhost.requests_per_minute) }} req/min
            </template>
          </span>
          <span class="fleet-pressure__vhost-meta">
            <template v-if="server.busiest_vhost.edge_only">
              client IPs not visible (Cloudflare edge)
            </template>
            <template v-else-if="server.busiest_vhost.distinct_clients !== null">
              {{ server.busiest_vhost.distinct_clients }} distinct client(s)
            </template>
            <template v-if="server.busiest_vhost.error_pct">
              · {{ Math.round(server.busiest_vhost.error_pct) }}% errors
            </template>
          </span>
        </span>
        <router-link
          class="fleet-pressure__vhost-link"
          :to="`/logs?domain=${encodeURIComponent(server.busiest_vhost.domain)}&server_id=${server.server_id}`"
        >Logs →</router-link>
      </div>

      <p v-if="!server.fpm_pools.length" class="fleet-pressure__note">
        No PHP-FPM pools reported on this server.
      </p>
    </section>

    <section v-if="rest.length" class="fleet-pressure__card">
      <header class="fleet-pressure__card-head">
        <span class="fleet-pressure__card-name">Rest of fleet</span>
      </header>
      <router-link
        v-for="server in rest"
        :key="server.server_id"
        class="fleet-pressure__brief"
        :to="`/servers/${server.server_id}`"
      >
        <span class="fleet-pressure__brief-name">{{ server.server }}</span>
        <span class="fleet-pressure__meta">
          {{ server.cores !== null ? `${server.cores} cores` : '—' }}
        </span>
        <span class="fleet-pressure__bar">
          <span
            class="fleet-pressure__bar-fill"
            :class="`is-${loadTone(server.load_per_core)}`"
            :style="{ width: `${loadWidth(server.load_per_core)}%` }"
          ></span>
        </span>
        <span class="fleet-pressure__brief-figure">
          {{ server.load_per_core !== null ? server.load_per_core.toFixed(2) : '—' }}/core
        </span>
        <span class="fleet-pressure__brief-figure">
          disk {{ server.disk_pct !== null ? `${Math.round(server.disk_pct)}%` : '—' }}
        </span>
        <span class="fleet-pressure__pill" :class="`is-${worstPill(server) ? worstPill(server).tone : 'good'}`">
          {{ worstPill(server) ? worstPill(server).label : 'Healthy' }}
        </span>
      </router-link>
    </section>

    <section v-if="notReporting.length" class="fleet-pressure__card">
      <header class="fleet-pressure__card-head">
        <span class="fleet-pressure__card-name">Not reporting pressure</span>
        <span class="fleet-pressure__meta">
          Listed separately, not sorted in at zero — an agent we know nothing
          about is unknown, not idle.
        </span>
      </header>
      <router-link
        v-for="server in notReporting"
        :key="server.server_id"
        class="fleet-pressure__brief"
        :to="`/servers/${server.server_id}`"
      >
        <span class="fleet-pressure__brief-name">{{ server.server }}</span>
        <span class="fleet-pressure__meta">{{ server.agent_status }}</span>
      </router-link>
    </section>
  </div>
</template>

<script setup>
/**
 * Fleet pressure — mounted at `/servers/pressure` by
 * `app\module\observability\Controller\FleetDisplayController::pressure()`.
 *
 * The 2026-08-01 UI review's Medium finding: pressure telemetry "arrives from
 * every agent — but only as panels inside a single server's detail page. There
 * is no fleet-wide view, which is precisely the view that answers 'which box
 * is about to fall over?'"
 *
 * One row per server, one row per resource, ordered by whatever is closest to
 * hurting (the server-side `pressure_score`). Reads a single fleet endpoint,
 * never a per-server fan-out.
 *
 * **The FPM row is the one that matters most** — it is the signal that went
 * from healthy to "every site is down" in under a minute — so it names the
 * pool and prints the ratio, not a percentage alone. A pool whose numbers
 * cannot be true (the 320%-for-`www` attribution bug, fixed in luna
 * staging.22) is marked "reading suspect" rather than hidden: an operator who
 * saw 320% in production is better served by an admission than by silence.
 */
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import useApi from '@apexl/icarus-core/src/services/api';
import commonProps from '@apexl/icarus-core/src/components/_properties/common';

const props = defineProps({
  ...commonProps,
  apiBase: { type: String, default: '/api/v1/observability/fleet' },
});

const SUSPECT_HINT = 'This pool reported more busy workers than it can have. '
  + 'Known agent attribution bug, fixed in luna staging.22 — the figure is '
  + 'shown but excluded from the pressure ordering.';

/** Matches the agent's own sampling cadence closely enough to feel live. */
const POLL_MS = 20000;

/** How many servers get the full breakdown before the rest collapse to a row each. */
const DETAILED_COUNT = 1;

const api = useApi();
const servers = ref([]);
const notReporting = ref([]);
const generatedAt = ref(null);
const loading = ref(true);
const loadError = ref(null);
let pollHandle = null;

async function load() {
  try {
    const data = await api.get(`${props.apiBase}/pressure`);
    servers.value = data.servers || [];
    notReporting.value = data.not_reporting || [];
    generatedAt.value = data.generated_at || null;
    loadError.value = null;
  } catch {
    loadError.value = 'Could not load fleet pressure — check your connection and try again.';
  } finally {
    loading.value = false;
  }
}

onMounted(() => {
  load();
  pollHandle = setInterval(load, POLL_MS);
});

onBeforeUnmount(() => {
  if (pollHandle) {
    clearInterval(pollHandle);
  }
});

const detailed = computed(() => servers.value.slice(0, DETAILED_COUNT));
const rest = computed(() => servers.value.slice(DETAILED_COUNT));

const generatedLabel = computed(() => (generatedAt.value
  ? relativeAge(Math.max(0, Math.round(Date.now() / 1000) - generatedAt.value))
  : ''));

/** Busiest mounts first, and never more than four — a box with 30 loopback mounts shouldn't bury the rest of the card. */
function filesystems(server) {
  return [...(server.filesystems || [])]
    .filter((fs) => fs && typeof fs.usedPct === 'number')
    .sort((a, b) => b.usedPct - a.usedPct)
    .slice(0, 4);
}

function memoryNote(server) {
  const mem = server.memory;
  if (!mem || typeof mem.totalBytes !== 'number') {
    return '';
  }

  return `${formatBytes(mem.usedBytes)} of ${formatBytes(mem.totalBytes)}`;
}

/**
 * The single worst thing about this server, as a pill. Suspect FPM readings
 * are deliberately not eligible — badging a box "Saturated" on a number we
 * have just admitted we don't believe would be the same mistake twice.
 */
function worstPill(server) {
  if (server.load_per_core !== null && server.load_per_core >= 1.25) {
    return { label: 'CPU', tone: 'bad' };
  }
  const pool = (server.fpm_pools || []).find(
    (p) => !p.saturation_fault && p.saturation_pct !== null && p.saturation_pct >= 90,
  );
  if (pool) {
    return { label: 'FPM', tone: 'bad' };
  }
  if (server.disk_pct !== null && server.disk_pct >= 90) {
    return { label: 'Disk', tone: 'bad' };
  }
  if (server.disk_pct !== null && server.disk_pct >= 75) {
    return { label: 'Disk', tone: 'warn' };
  }
  if (server.mem_pct !== null && server.mem_pct >= 90) {
    return { label: 'Memory', tone: 'bad' };
  }
  if (server.load_per_core !== null && server.load_per_core >= 0.75) {
    return { label: 'Web pressure', tone: 'warn' };
  }
  if (server.saturation_fault) {
    return { label: 'Reading suspect', tone: 'unknown' };
  }

  return null;
}

function poolLabel(pool) {
  return pool.php_version ? `php${pool.php_version}/${pool.pool}` : pool.pool;
}

function methodLabel(method) {
  return method === 'status'
    ? 'from the FPM status page'
    : 'by worker-process count';
}

function poolWidth(pool) {
  if (pool.saturation_pct === null) return 0;
  return Math.max(2, Math.min(100, pool.saturation_pct));
}

function poolTone(pool) {
  if (pool.saturation_fault) return 'unknown';
  if (pool.saturation_pct === null) return 'unknown';
  if (pool.saturation_pct >= 90) return 'bad';
  if (pool.saturation_pct >= 75) return 'warn';
  return 'good';
}

/** 300 req/min is `web.request_flood_by_vhost`'s threshold — colour approaches it. */
function vhostTone(vhost) {
  const rpm = vhost.requests_per_minute;
  if (rpm === null || rpm === undefined) return 'unknown';
  if (rpm >= 300) return 'bad';
  if (rpm >= 150) return 'warn';
  return 'good';
}

function loadWidth(loadPerCore) {
  if (loadPerCore === null || loadPerCore === undefined) return 0;
  return Math.max(2, Math.min(100, (loadPerCore / 1.25) * 100));
}

function loadTone(loadPerCore) {
  if (loadPerCore === null || loadPerCore === undefined) return 'unknown';
  if (loadPerCore >= 1.25) return 'bad';
  if (loadPerCore >= 0.75) return 'warn';
  return 'good';
}

function pctTone(pct) {
  if (pct === null || pct === undefined) return 'unknown';
  if (pct >= 90) return 'bad';
  if (pct >= 75) return 'warn';
  return 'good';
}

function clampPct(pct) {
  if (pct === null || pct === undefined) return 0;
  return Math.max(0, Math.min(100, pct));
}

function formatBytes(bytes) {
  if (typeof bytes !== 'number' || !Number.isFinite(bytes) || bytes < 0) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }

  return `${value >= 10 || unit === 0 ? Math.round(value) : value.toFixed(1)} ${units[unit]}`;
}

function relativeAge(seconds) {
  if (seconds === null || seconds === undefined) return '';
  if (seconds < 90) return `${Math.round(seconds)}s ago`;
  if (seconds < 5400) return `${Math.round(seconds / 60)}m ago`;
  if (seconds < 172800) return `${Math.round(seconds / 3600)}h ago`;
  return `${Math.round(seconds / 86400)}d ago`;
}
</script>

<style lang="scss">
.fleet-pressure {
  &__head {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    gap: 16px;
    margin-bottom: 18px;
    flex-wrap: wrap;

    h1 {
      font-size: 20px;
      font-weight: 600;
      margin: 0 0 4px;
    }

    p {
      font-size: 13px;
      color: var(--theme-text-secondary);
      margin: 0;
      max-width: 74ch;
    }
  }

  &__back {
    font-family: var(--theme-font-mono);
    font-size: 11px;
    color: var(--theme-accent-dark);
    font-weight: 600;
    text-decoration: none;
    white-space: nowrap;
  }

  &__error {
    background: #f8dede;
    color: #a53232;
    border-radius: var(--theme-radius, 6px);
    padding: 10px 14px;
    font-size: 12.5px;
  }

  &__empty, &__note {
    padding: 14px;
    margin: 0;
    font-size: 12.5px;
    color: var(--theme-text-tertiary);
  }

  &__card {
    background: var(--theme-bg-card);
    border: 1px solid var(--theme-border);
    border-radius: var(--theme-radius-lg, 10px);
    box-shadow: var(--theme-shadow-card);
    overflow: hidden;
    margin-bottom: 14px;
  }

  &__card-head {
    display: flex;
    align-items: baseline;
    gap: 10px;
    padding: 11px 14px;
    border-bottom: 1px solid var(--theme-border);
    flex-wrap: wrap;
  }

  &__card-name {
    font-size: 13px;
    font-weight: 620;
    font-family: var(--theme-font-mono);
    color: inherit;
    text-decoration: none;
  }

  &__meta {
    font-size: 11px;
    color: var(--theme-text-tertiary);
  }

  &__row {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 14px;
    border-bottom: 1px solid var(--theme-border-soft);
    font-size: 12px;

    &:last-child { border-bottom: 0; }
  }

  &__row-label {
    flex: 0 0 82px;
    font-family: var(--theme-font-mono);
    font-size: 11px;
    color: var(--theme-text-secondary);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__row-value {
    flex: 0 0 auto;
    min-width: 92px;
    font-family: var(--theme-font-mono);
    font-variant-numeric: tabular-nums;

    &--wide { min-width: 190px; }

    strong { font-weight: 700; }
  }

  &__row-note {
    flex: 1;
    text-align: right;
    font-family: var(--theme-font-mono);
    font-size: 10.5px;
    color: var(--theme-text-tertiary);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__suspect {
    color: var(--theme-status-warning);
    font-weight: 700;
    border-bottom: 1px dotted currentColor;
    cursor: help;
  }

  &__bar {
    flex: 0 0 120px;
    height: 6px;
    border-radius: 3px;
    background: var(--theme-border-soft);
    overflow: hidden;
    display: block;
  }

  &__bar-fill {
    display: block;
    height: 100%;
    border-radius: 3px;
    background: var(--theme-status-success);

    &.is-warn { background: var(--theme-status-warning); }
    &.is-bad  { background: var(--theme-status-danger); }
    &.is-unknown {
      background: repeating-linear-gradient(
        45deg,
        var(--theme-border-strong), var(--theme-border-strong) 4px,
        var(--theme-border-soft), var(--theme-border-soft) 8px
      );
    }
  }

  &__vhost {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 9px 14px;
    background: #fdf1de;
    border-top: 1px solid var(--theme-border-soft);
    font-size: 12px;
  }

  &__stripe {
    width: 3px;
    align-self: stretch;
    border-radius: 2px;
    flex: none;

    &.is-bad  { background: var(--theme-status-danger); }
    &.is-warn { background: var(--theme-status-warning); }
    &.is-good { background: var(--theme-status-success); }
    &.is-unknown { background: var(--theme-border-strong); }
  }

  &__vhost-body { flex: 1; min-width: 0; }

  &__vhost-title { display: block; }

  &__vhost-meta {
    display: block;
    font-family: var(--theme-font-mono);
    font-size: 10.5px;
    color: var(--theme-text-secondary);
    margin-top: 2px;
  }

  &__vhost-link {
    flex: none;
    font-family: var(--theme-font-mono);
    font-size: 10.5px;
    font-weight: 600;
    color: var(--theme-accent-dark);
    text-decoration: none;
  }

  &__brief {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 9px 14px;
    border-bottom: 1px solid var(--theme-border-soft);
    font-size: 12px;
    color: inherit;
    text-decoration: none !important;

    &:last-child { border-bottom: 0; }
    &:hover { background: var(--theme-bg-content); }
  }

  &__brief-name {
    flex: 0 0 160px;
    font-family: var(--theme-font-mono);
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__brief-figure {
    flex: 0 0 auto;
    min-width: 84px;
    text-align: right;
    font-family: var(--theme-font-mono);
    font-size: 11px;
    color: var(--theme-text-tertiary);
    font-variant-numeric: tabular-nums;
  }

  &__pill {
    flex: none;
    font-size: 9.5px;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    padding: 2.5px 8px;
    border-radius: 20px;
    font-weight: 700;
    white-space: nowrap;
    margin-left: auto;

    &.is-bad  { background: #fbe9e6; color: #a53232; }
    &.is-warn { background: #fdf1de; color: #8a5a0d; }
    &.is-good { background: #e6f2ec; color: #1c7a4d; }
    &.is-unknown { background: var(--theme-border-soft); color: var(--theme-text-tertiary); }
  }
}
</style>
